// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import ListingCategories from "@gcg/App/Components/ListingCategories/ListingCategories";
import { getBrandCategories } from "@gcg/App/Services/BrandCategoriesService";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    BrandCategoriesStateContext,
    BrandStateContext,
} from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import { BrandCategoryModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import sortBy from "lodash/sortBy";
import React, { FC, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    showImages = "showImages",
    uppercaseTitle = "uppercaseTitle",
    boldTitle = "boldTitle",
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.showImages]: boolean;
        [fields.uppercaseTitle]: boolean;
        [fields.boldTitle]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({});

const mapDispatchToProps = {};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface BrandDetailsCategoriesStyles {
    title?: TypographyProps;
}

export const categoriesStyles: BrandDetailsCategoriesStyles = {
    title: {
        variant: "h3",
    },
};

const styles = categoriesStyles;

const BrandDetailsCategories: FC<Props> = ({ fields }) => {
    const { value: brand } = useContext(BrandStateContext);
    const { isLoading, value: brandCategories } = useContext(BrandCategoriesStateContext);

    const [loading, setLoading] = useState(isLoading);
    const [neededBrandCategories, setNeededBrandCategories] = useState([] as BrandCategoryModel[]);
    const [sortedCategories, setSortedCategories] = useState([] as BrandCategoryModel[]);

    useEffect(() => {
        if (brandCategories) {
            if (brandCategories.length) {
                setNeededBrandCategories(brandCategories);

                return;
            }

            setLoading(true);

            getBrandCategories({
                brandId: brand?.id || "",
                page: 1,
                pageSize: 1000,
                sort: "name",
                maximumDepth: 3,
            }).then(
                (response: any) => {
                    setLoading(false);

                    const brandCategories = response.brandCategories;

                    setNeededBrandCategories(brandCategories);
                },
                (error: any) => {
                    setLoading(false);
                },
            );
        }
    }, [brandCategories]);

    useEffect(() => {
        if (neededBrandCategories.length) {
            const allCategories = [] as BrandCategoryModel[];

            neededBrandCategories.forEach(brandCategory => {
                allCategories.push(brandCategory);

                if (brandCategory.subCategories && brandCategory.subCategories?.length) {
                    brandCategory.subCategories.forEach((subCat: BrandCategoryModel) => {
                        const isDuplicated = allCategories.some(
                            (category: BrandCategoryModel) => subCat.categoryId !== category.categoryId,
                        );

                        if (!isDuplicated) {
                            allCategories.push(subCat);
                        }
                    });
                }
            });

            if (allCategories.length > 0) {
                const sortedCategories: BrandCategoryModel[] = sortBy(
                    allCategories,
                    [category => category.categoryName],
                    ["asc"],
                );

                setSortedCategories(sortedCategories);
            }
        }
    }, [neededBrandCategories]);

    if (!loading && !sortedCategories.length) {
        return null;
    }

    if (loading) {
        return (
            <StyledWrapper
                css={css`
                    height: 150px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                `}
            >
                <LoadingSpinner color="primary.main" />
            </StyledWrapper>
        );
    }

    const { title, showImages } = fields;

    return (
        <StyledWrapper data-test-selector="brandCategories">
            <Typography
                {...styles.title}
                css={css`
                    font-weight: ${fields.boldTitle ? "600" : "normal"};
                    text-transform: ${fields.uppercaseTitle ? "uppercase" : "initial"};
                    letter-spacing: 0.3px;
                `}
                data-test-selector="brandCategoriesTitle"
            >
                {title}
            </Typography>
            <ListingCategories usage="categories" items={sortedCategories} brand={brand} showImages={showImages} />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(BrandDetailsCategories),
    definition: {
        group: "Brand Details",
        displayName: "Categories",
        allowedContexts: ["BrandDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Shop By Category",
                fieldType: "Translatable",
            },
            {
                name: fields.showImages,
                displayName: "Show Images",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.uppercaseTitle,
                displayName: "Uppercase Title",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.boldTitle,
                displayName: "Bold Title",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
